import React, { useState, useCallback } from 'react';
import { ordersAPI, shopsAPI } from '../services/api';
import { Order, Shop } from '../types';
import './BusinessReport.css';

type Period = 'today' | 'week' | 'month' | 'last_month' | 'quarter' | 'year' | 'custom';
interface DateRange { from: string; to: string; }

function getRange(period: Period, custom: DateRange): DateRange {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const today = fmt(now);
  if (period === 'today') return { from: today, to: today };
  if (period === 'week') { const d = new Date(now); d.setDate(d.getDate() - 6); return { from: fmt(d), to: today }; }
  if (period === 'month') return { from: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`, to: today };
  if (period === 'last_month') {
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: fmt(first), to: fmt(last) };
  }
  if (period === 'quarter') { const d = new Date(now); d.setMonth(d.getMonth() - 2); d.setDate(1); return { from: fmt(d), to: today }; }
  if (period === 'year') return { from: `${now.getFullYear()}-01-01`, to: today };
  return custom;
}

const PERIOD_LABELS: Record<Period, string> = {
  today: 'Сегодня', week: '7 дней', month: 'Этот месяц',
  last_month: 'Прошлый месяц', quarter: '3 месяца', year: 'Этот год', custom: 'Свой период',
};

const fmtNum = (n: number) => n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtK = (n: number) => {
  if (n >= 1_000_000) return (n / 1_000_000).toLocaleString('ru-RU', { maximumFractionDigits: 1 }) + ' млн';
  if (n >= 1_000) return (n / 1_000).toLocaleString('ru-RU', { maximumFractionDigits: 1 }) + ' тыс';
  return fmtNum(n);
};

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Ожидает', CONFIRMED: 'Подтверждён', SHIPPED: 'Отправлен',
  DELIVERED: 'Доставлен', CANCELLED: 'Отменён', REJECTED: 'Отклонён',
};
const STATUS_COLORS: Record<string, string> = {
  PENDING: '#f59e0b', CONFIRMED: '#3b82f6', SHIPPED: '#8b5cf6',
  DELIVERED: '#10b981', CANCELLED: '#ef4444', REJECTED: '#dc2626',
};

interface Metrics {
  revenue: number; cost: number; profit: number; margin: number;
  orderCount: number; avgOrder: number;
  totalByStatus: Record<string, number>;
  topProducts: { name: string; revenue: number; profit: number; qty: number }[];
  topShops: { name: string; revenue: number; orderCount: number }[];
  totalDebt: number; totalAdvance: number;
  dailyRevenue: { date: string; revenue: number; profit: number }[];
}

function calcMetrics(orders: Order[], shops: Shop[], range: DateRange): Metrics {
  const delivered = orders.filter(o => o.status === 'DELIVERED');
  let revenue = 0, cost = 0;
  const productMap: Record<string, { name: string; revenue: number; profit: number; qty: number }> = {};
  const shopMap: Record<string, { name: string; revenue: number; orderCount: number }> = {};
  const dayMap: Record<string, { revenue: number; profit: number }> = {};

  for (const order of delivered) {
    let orderCost = 0;
    for (const item of order.items ?? []) {
      const r = Number(item.unitPrice) * Number(item.quantity);
      const c = Number((item as any).unitCostPrice ?? 0) * Number(item.quantity);
      orderCost += c;
      const pName = item.product?.name ?? `#${item.productId}`;
      if (!productMap[pName]) productMap[pName] = { name: pName, revenue: 0, profit: 0, qty: 0 };
      productMap[pName].revenue += r;
      productMap[pName].profit += r - c;
      productMap[pName].qty += Number(item.quantity);
    }
    const rev = Number(order.totalAmount);
    revenue += rev; cost += orderCost;
    const sName = order.shop?.name ?? '—';
    if (!shopMap[sName]) shopMap[sName] = { name: sName, revenue: 0, orderCount: 0 };
    shopMap[sName].revenue += rev; shopMap[sName].orderCount++;
    const day = order.createdAt.slice(0, 10);
    if (!dayMap[day]) dayMap[day] = { revenue: 0, profit: 0 };
    dayMap[day].revenue += rev; dayMap[day].profit += rev - orderCost;
  }

  const totalByStatus: Record<string, number> = {};
  for (const o of orders) totalByStatus[o.status] = (totalByStatus[o.status] ?? 0) + 1;

  const dailyRevenue: { date: string; revenue: number; profit: number }[] = [];
  const end = new Date(range.to);
  for (let d = new Date(range.from); d <= end; d.setDate(d.getDate() + 1)) {
    const key = d.toISOString().slice(0, 10);
    dailyRevenue.push({ date: key, revenue: dayMap[key]?.revenue ?? 0, profit: dayMap[key]?.profit ?? 0 });
  }

  const profit = revenue - cost;
  return {
    revenue, cost, profit, margin: revenue > 0 ? (profit / revenue) * 100 : 0,
    orderCount: delivered.length,
    avgOrder: delivered.length ? revenue / delivered.length : 0,
    totalByStatus,
    topProducts: Object.values(productMap).sort((a, b) => b.revenue - a.revenue).slice(0, 8),
    topShops: Object.values(shopMap).sort((a, b) => b.revenue - a.revenue).slice(0, 6),
    totalDebt: shops.reduce((s, sh) => s + Number(sh.debt ?? 0), 0),
    totalAdvance: shops.reduce((s, sh) => s + Number(sh.advance ?? 0), 0),
    dailyRevenue,
  };
}

export const BusinessReport: React.FC = () => {

  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState<Period>('month');
  const [custom, setCustom] = useState<DateRange>({ from: '', to: '' });
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [rangeLabel, setRangeLabel] = useState('');

  const load = useCallback(async (p: Period = period, c: DateRange = custom) => {
    const range = getRange(p, c);
    if (p === 'custom' && (!range.from || !range.to)) return;
    setLoading(true);
    try {
      const [ordersRes, shopsRes] = await Promise.all([
        ordersAPI.getAll({ limit: 5000, dateFrom: range.from, dateTo: range.to }),
        shopsAPI.getAll({ limit: 500 }),
      ]);
      setMetrics(calcMetrics(ordersRes.data, shopsRes.data, range));
      const rev = (s: string) => s.split('-').reverse().join('.');
      setRangeLabel(range.from === range.to ? rev(range.from) : `${rev(range.from)} — ${rev(range.to)}`);
    } finally {
      setLoading(false);
    }
  }, [period, custom]);

  const selectPeriod = (p: Period) => { setPeriod(p); if (p !== 'custom') load(p, custom); };

  const maxRev = metrics ? Math.max(...metrics.dailyRevenue.map(d => d.revenue), 1) : 1;

  return (
    <>
      <button className="report-trigger-btn" onClick={() => { setOpen(true); load(); }}>
        📊 Отчёт по бизнесу
      </button>

      {open && (
        <div className="report-overlay" onClick={() => setOpen(false)}>
          <div className="report-panel" onClick={e => e.stopPropagation()}>

            <div className="report-header">
              <div>
                <h2 className="report-title">📊 Бизнес-отчёт</h2>
                {rangeLabel && <div className="report-range">{rangeLabel}</div>}
              </div>
              <button className="report-close" onClick={() => setOpen(false)}>✕</button>
            </div>

            <div className="report-periods">
              {(Object.keys(PERIOD_LABELS) as Period[]).map(p => (
                <button key={p} className={`period-btn ${period === p ? 'period-active' : ''}`} onClick={() => selectPeriod(p)}>
                  {PERIOD_LABELS[p]}
                </button>
              ))}
            </div>

            {period === 'custom' && (
              <div className="report-custom-range">
                <input type="date" value={custom.from} onChange={e => setCustom(c => ({ ...c, from: e.target.value }))} />
                <span>—</span>
                <input type="date" value={custom.to} onChange={e => setCustom(c => ({ ...c, to: e.target.value }))} />
                <button className="report-load-btn-sm" onClick={() => load(period, custom)}>Показать</button>
              </div>
            )}

            {loading && <div className="report-loading">⏳ Загрузка данных...</div>}

            {metrics && !loading && (
              <div className="report-body">

                {/* KPIs */}
                <div className="report-kpis">
                  {[
                    { icon: '💰', val: fmtK(metrics.revenue) + ' с', lbl: 'Выручка', cls: 'kpi-green' },
                    { icon: '📦', val: fmtK(metrics.cost) + ' с', lbl: 'Себестоимость', cls: 'kpi-red' },
                    { icon: metrics.profit >= 0 ? '📈' : '📉', val: fmtK(metrics.profit) + ' с', lbl: 'Прибыль', cls: metrics.profit >= 0 ? 'kpi-blue' : 'kpi-orange' },
                    { icon: '🎯', val: metrics.margin.toFixed(1) + '%', lbl: 'Маржа', cls: 'kpi-purple' },
                    { icon: '✅', val: String(metrics.orderCount), lbl: 'Доставлено заказов', cls: 'kpi-cyan' },
                    { icon: '🧾', val: fmtK(metrics.avgOrder) + ' с', lbl: 'Средний чек', cls: 'kpi-amber' },
                  ].map(k => (
                    <div key={k.lbl} className={`rk ${k.cls}`}>
                      <div className="rk-icon">{k.icon}</div>
                      <div className="rk-val">{k.val}</div>
                      <div className="rk-lbl">{k.lbl}</div>
                    </div>
                  ))}
                </div>

                {/* Debt strip */}
                <div className="report-debt-strip">
                  <div className="ds-item ds-debt">
                    <span className="ds-lbl">Текущий долг магазинов</span>
                    <span className="ds-val">{fmtNum(metrics.totalDebt)} с</span>
                  </div>
                  <div className="ds-item ds-adv">
                    <span className="ds-lbl">Аванс магазинов</span>
                    <span className="ds-val">+{fmtNum(metrics.totalAdvance)} с</span>
                  </div>
                  <div className="ds-item ds-net">
                    <span className="ds-lbl">Нетто долг</span>
                    <span className="ds-val">{fmtNum(metrics.totalDebt - metrics.totalAdvance)} с</span>
                  </div>
                </div>

                {/* Chart */}
                {metrics.dailyRevenue.length > 1 && (
                  <div className="report-card">
                    <h3 className="rc-title">Выручка и прибыль по дням</h3>
                    <div className="mini-chart">
                      {metrics.dailyRevenue.map(d => (
                        <div key={d.date} className="mc-col" title={`${d.date.split('-').reverse().join('.')}\nВыручка: ${fmtNum(d.revenue)} с\nПрибыль: ${fmtNum(d.profit)} с`}>
                          <div className="mc-bars">
                            <div className="mc-bar-bg" style={{ height: `${Math.max(d.revenue > 0 ? 3 : 0, (d.revenue / maxRev) * 100)}%` }} />
                            <div className="mc-bar-fg" style={{ height: `${Math.max(d.profit > 0 ? 3 : 0, (Math.max(0, d.profit) / maxRev) * 100)}%` }} />
                          </div>
                          {metrics.dailyRevenue.length <= 31 && <div className="mc-lbl">{d.date.slice(8)}</div>}
                        </div>
                      ))}
                    </div>
                    <div className="mc-legend">
                      <span><i className="leg-dot" style={{ background: '#bfdbfe' }} /> Выручка</span>
                      <span><i className="leg-dot" style={{ background: '#10b981' }} /> Прибыль</span>
                    </div>
                  </div>
                )}

                <div className="report-two-col">
                  {/* Statuses */}
                  <div className="report-card">
                    <h3 className="rc-title">Статусы заказов за период</h3>
                    {Object.keys(metrics.totalByStatus).length === 0
                      ? <p className="rc-empty">Нет заказов</p>
                      : (() => {
                        const total = Object.values(metrics.totalByStatus).reduce((a, b) => a + b, 0);
                        return Object.entries(metrics.totalByStatus).map(([s, cnt]) => (
                          <div key={s} className="ss-row">
                            <span className="ss-dot" style={{ background: STATUS_COLORS[s] ?? '#9ca3af' }} />
                            <span className="ss-name">{STATUS_LABELS[s] ?? s}</span>
                            <div className="ss-bar-wrap">
                              <div className="ss-bar" style={{ width: `${total ? Math.round(cnt / total * 100) : 0}%`, background: STATUS_COLORS[s] ?? '#9ca3af' }} />
                            </div>
                            <span className="ss-cnt">{cnt}</span>
                          </div>
                        ));
                      })()
                    }
                  </div>

                  {/* Top shops */}
                  <div className="report-card">
                    <h3 className="rc-title">Топ магазины по выручке</h3>
                    {metrics.topShops.length === 0
                      ? <p className="rc-empty">Нет данных</p>
                      : metrics.topShops.map((s, i) => (
                        <div key={s.name} className="top-row">
                          <span className="top-num">{i + 1}</span>
                          <span className="top-name">{s.name}</span>
                          <div className="top-right">
                            <span className="top-val">{fmtK(s.revenue)} с</span>
                            <span className="top-sub">{s.orderCount} зак.</span>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>

                {/* Top products */}
                <div className="report-card">
                  <h3 className="rc-title">Топ товары</h3>
                  {metrics.topProducts.length === 0
                    ? <p className="rc-empty">Нет данных</p>
                    : <div className="pt-table">
                      <div className="pt-head">
                        <span className="pt-c1">Товар</span>
                        <span className="pt-cn">Кол-во</span>
                        <span className="pt-cn">Выручка</span>
                        <span className="pt-cn">Прибыль</span>
                        <span className="pt-cn">Маржа</span>
                      </div>
                      {metrics.topProducts.map(p => (
                        <div key={p.name} className="pt-row">
                          <span className="pt-c1">{p.name}</span>
                          <span className="pt-cn">{p.qty.toLocaleString('ru-RU', { maximumFractionDigits: 1 })}</span>
                          <span className="pt-cn">{fmtK(p.revenue)} с</span>
                          <span className={`pt-cn ${p.profit >= 0 ? 'pos' : 'neg'}`}>{fmtK(p.profit)} с</span>
                          <span className={`pt-cn ${p.profit >= 0 ? 'pos' : 'neg'}`}>
                            {p.revenue > 0 ? (p.profit / p.revenue * 100).toFixed(1) + '%' : '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  }
                </div>

              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BusinessReport;
